function escapeSqlString(value) {
  return String(value || '').replace(/'/g, "''")
}

function formatDateTimeStart(date) {
  return `${date} 00:00:00`
}

function getExclusiveEndDate(date) {
  const target = new Date(date)
  target.setDate(target.getDate() + 1)
  const year = target.getFullYear()
  const month = String(target.getMonth() + 1).padStart(2, '0')
  const day = String(target.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function normalizeVersion(version) {
  return String(version || 'v4').toLowerCase() === 'v3' ? 'v3' : 'v4'
}

function getFlowType(version) {
  return normalizeVersion(version) === 'v3' ? 'flowSource' : 'visit'
}

function getClickTypeCondition(version) {
  return normalizeVersion(version) === 'v4' ? "\nAND     type = 'click'" : ''
}

function buildSelectPrefix({ title, mode, includeChannel = false, indexName = '' }) {
  const select = [`SELECT  '${title}' AS title`]
  if (mode === 'dedup') {
    select.push("        ,'__dedup__' AS time_scope")
  } else {
    select.push("        ,TO_CHAR(FROM_UNIXTIME(t_timestamp / 1000),'yyyy-mm-dd') AS time_scope")
  }
  if (includeChannel) {
    select.push('        ,channel')
  }
  if (indexName) {
    select.push(`        ,'${escapeSqlString(indexName)}' AS index_name`)
  }
  return select.join('\n')
}

function buildGroupBy(mode, includeChannel = false, extras = []) {
  if (mode === 'dedup') {
    const dedupGroups = [...extras]
    return dedupGroups.length ? `GROUP BY ${dedupGroups.join(', ')}` : ''
  }
  const groups = ['time_scope']
  if (includeChannel) groups.push('channel')
  groups.push(...extras)
  return `GROUP BY ${groups.join(', ')}`
}

function wrapDailySumSql(sql, metrics, includeChannel = false, includeIndexName = false) {
  const parts = ["SELECT  title", '        ,time_scope']
  if (includeChannel) parts.push('        ,channel')
  if (includeIndexName) parts.push('        ,index_name')
  metrics.forEach((metric) => {
    parts.push(`        ,SUM(${metric}) OVER (${buildWindowPartition(includeChannel, includeIndexName)} ORDER BY time_scope ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW) AS ${metric}`)
  })
  return `${parts.join('\n')}\nFROM (\n${indentSql(sql)}\n) AS base`
}

function buildWindowPartition(includeChannel, includeIndexName) {
  const fields = ['title']
  if (includeChannel) fields.push('channel')
  if (includeIndexName) fields.push('index_name')
  return `PARTITION BY ${fields.join(', ')}`
}

function indentSql(sql) {
  return String(sql || '')
    .split('\n')
    .map((line) => `  ${line}`)
    .join('\n')
}

function buildFlowSql({ projectCode, version, startDate, endDate, mode, byChannel }) {
  const base = `${buildSelectPrefix({ title: '流量数据', mode, includeChannel: byChannel })}
        ,COUNT(user_id) pv
        ,COUNT(DISTINCT user_id) uv
FROM    mf_tracking_data_origin_record
WHERE   project_code = '${escapeSqlString(projectCode)}'
AND     type = '${getFlowType(version)}'
AND     t_timestamp >= TO_MILLIS(DATETIME '${formatDateTimeStart(startDate)}')
AND     t_timestamp < TO_MILLIS(DATETIME '${formatDateTimeStart(getExclusiveEndDate(endDate))}')
${buildGroupBy(mode, byChannel)}
;`
  if (mode === 'dailySum') {
    return `${wrapDailySumSql(buildFlowSql({ projectCode, version, startDate, endDate, mode: 'daily', byChannel }), ['pv', 'uv'], byChannel)}\n;`
  }
  return base
}

function buildClickSql({ projectCode, version, startDate, endDate, mode, byChannel, clickKeyword = '点击' }) {
  const base = `${buildSelectPrefix({ title: '点击数据', mode, includeChannel: byChannel })}
        ,COUNT(user_id) pv
        ,COUNT(DISTINCT user_id) uv
FROM    mf_tracking_data_origin_record
WHERE   project_code = '${escapeSqlString(projectCode)}'
AND     t_timestamp >= TO_MILLIS(DATETIME '${formatDateTimeStart(startDate)}')
AND     t_timestamp < TO_MILLIS(DATETIME '${formatDateTimeStart(getExclusiveEndDate(endDate))}')
AND     custom_type IS NOT NULL
AND     custom_type != ''
AND     custom_type LIKE '%${escapeSqlString(clickKeyword)}%'${getClickTypeCondition(version)}
${buildGroupBy(mode, byChannel)}
;`
  if (mode === 'dailySum') {
    return `${wrapDailySumSql(buildClickSql({ projectCode, version, startDate, endDate, mode: 'daily', byChannel, clickKeyword }), ['pv', 'uv'], byChannel)}\n;`
  }
  return base
}

function buildClickPdpSql({ projectCode, startDate, endDate, mode, byChannel }) {
  const base = `${buildSelectPrefix({ title: '点击PDP', mode, includeChannel: byChannel, indexName: '整体点击PDP' })}
        ,COUNT(user_id) pv
        ,COUNT(DISTINCT user_id) uv
FROM    mf_tracking_data_origin_record
WHERE   project_code = '${escapeSqlString(projectCode)}'
AND     t_timestamp >= TO_MILLIS(DATETIME '${formatDateTimeStart(startDate)}')
AND     t_timestamp < TO_MILLIS(DATETIME '${formatDateTimeStart(getExclusiveEndDate(endDate))}')
AND     custom_type LIKE '%-itemid-%'
${buildGroupBy(mode, byChannel, ['index_name'])}
;`
  if (mode === 'dailySum') {
    return `${wrapDailySumSql(buildClickPdpSql({ projectCode, startDate, endDate, mode: 'daily', byChannel }), ['pv', 'uv'], byChannel, true)}\n;`
  }
  return base
}

function buildStayTimeSql({ projectCode, startDate, endDate, mode, byChannel }) {
  const sumExpr = 'SUM(IF(value > 10 * 60 * 1000,0,value))'
  const base = `${buildSelectPrefix({ title: '停留时长', mode, includeChannel: byChannel })}
        ,COUNT(user_id) pv
        ,COUNT(DISTINCT user_id) uv
        ,${sumExpr} timeOnSite
        ,ROUND(${sumExpr} / 1000 / NULLIF(COUNT(user_id),0),2) avg_stay_seconds
FROM    mf_tracking_data_origin_record
WHERE   project_code = '${escapeSqlString(projectCode)}'
AND     t_timestamp >= TO_MILLIS(DATETIME '${formatDateTimeStart(startDate)}')
AND     t_timestamp < TO_MILLIS(DATETIME '${formatDateTimeStart(getExclusiveEndDate(endDate))}')
AND     type = 'timeOnSite'
AND     (custom_type IS NULL OR custom_type = '')
${buildGroupBy(mode, byChannel)}
;`
  if (mode === 'dailySum') {
    return `${wrapDailySumSql(buildStayTimeSql({ projectCode, startDate, endDate, mode: 'daily', byChannel }), ['pv', 'uv', 'timeOnSite'], byChannel)}\n;`
  }
  return base
}

function buildReturnVisitSql({ projectCode, version, startDate, endDate, mode, windowStartOffset, windowEndOffset, title }) {
  const flowType = getFlowType(version)
  const innerSql = `select project_code as code,
${mode === 'dedup' ? '' : 'visitDay as time_scope,'}
'visit-visit-day' as index_name,
CAST(count(DISTINCT user_id) as STRING) as uv
from (
    select t1.project_code,
           TO_CHAR(FROM_UNIXTIME(t1.t_timestamp / 1000),'yyyy-mm-dd') visitRecordDay,
           t1.user_id,
           t2.visitDay
    from mf_tracking_data_origin_record t1
    left join (
        select project_code,
               TO_CHAR(dateadd(FROM_UNIXTIME(t_timestamp / 1000),${windowStartOffset},'dd'),'yyyy-mm-dd') as visitStartDay,
               TO_CHAR(dateadd(FROM_UNIXTIME(t_timestamp / 1000),${windowEndOffset},'dd'),'yyyy-mm-dd') as visitEndDay,
               TO_CHAR(FROM_UNIXTIME(t_timestamp / 1000),'yyyy-mm-dd') visitDay,
               user_id
        from mf_tracking_data_origin_record
        where project_code in ('${escapeSqlString(projectCode)}')
          and type = '${flowType}'
          and t_timestamp >= TO_MILLIS(DATETIME '${formatDateTimeStart(startDate)}')
          and t_timestamp < TO_MILLIS(DATETIME '${formatDateTimeStart(getExclusiveEndDate(endDate))}')
        GROUP BY visitStartDay, visitEndDay, visitDay, user_id, project_code
    ) as t2
      on t1.project_code = t2.project_code
     and t1.user_id = t2.user_id
    where t1.project_code in ('${escapeSqlString(projectCode)}')
      and t1.type = '${flowType}'
      and TO_CHAR(FROM_UNIXTIME(t1.t_timestamp / 1000),'yyyy-mm-dd') >= t2.visitStartDay
      and TO_CHAR(FROM_UNIXTIME(t1.t_timestamp / 1000),'yyyy-mm-dd') <= t2.visitEndDay
    GROUP BY t1.project_code,
             TO_CHAR(FROM_UNIXTIME(t1.t_timestamp / 1000),'yyyy-mm-dd'),
             t1.user_id,
             t2.visitDay
) as t1
GROUP BY code${mode === 'dedup' ? '' : ', time_scope'}, index_name`

  const wrapped = `SELECT  '${title}' AS title
${mode === 'dedup' ? "        ,'__dedup__' AS time_scope" : '        ,time_scope'}
        ,index_name
        ,uv
FROM (
${indentSql(innerSql)}
) AS result
;`

  if (mode === 'dailySum') {
    return `${wrapDailySumSql(buildReturnVisitSql({ projectCode, version, startDate, endDate, mode: 'daily', windowStartOffset, windowEndOffset, title }), ['uv'], false, true)}\n;`
  }
  return wrapped
}

function buildOrderCrowdSql({ projectCode, startDate, endDate }) {
  const businessValue = new Date().toISOString().slice(2, 10).replace(/-/g, '')
  return `SELECT  '${escapeSqlString(projectCode)}' AS business_id
        ,'jumpItem' AS business_code
        ,'${businessValue}' AS business_value
        ,user_id
        ,SUBSTRING_INDEX(custom_type,'-',-1) AS item_ids
        ,TO_CHAR(FROM_UNIXTIME((MIN(t_timestamp)) / 1000),'yyyy-mm-dd hh:mi:ss') AS first_visit_time
FROM    mf_tracking_data_origin_record
WHERE   project_code = '${escapeSqlString(projectCode)}'
AND     custom_type LIKE '%-itemid-%'
AND     t_timestamp >= TO_MILLIS(DATETIME '${formatDateTimeStart(startDate)}')
AND     t_timestamp < TO_MILLIS(DATETIME '${formatDateTimeStart(getExclusiveEndDate(endDate))}')
GROUP BY user_id, item_ids
;`
}

export const PROJECT_EXPORT_CONTENTS = [
  { key: 'flow', label: '流量数据' },
  { key: 'click', label: '点击数据' },
  { key: 'clickPdp', label: '点击PDP' },
  { key: 'stayTime', label: '停留时长' },
  { key: 'return1', label: '次日回访' },
  { key: 'return7', label: '7日回访' },
  { key: 'orderCrowd', label: '抽订单人群 SQL' }
]

export const PROJECT_EXPORT_MODES = [
  { key: 'dailySum', label: '按天累加' },
  { key: 'dedup', label: '日期内去重' }
]

const CONTENT_BUILDERS = {
  flow: {
    modes: ['daily', 'dailySum', 'dedup'],
    build(params) {
      return buildFlowSql(params)
    }
  },
  click: {
    modes: ['daily', 'dailySum', 'dedup'],
    build(params) {
      return buildClickSql(params)
    }
  },
  clickPdp: {
    modes: ['daily', 'dailySum', 'dedup'],
    build(params) {
      return buildClickPdpSql(params)
    }
  },
  stayTime: {
    modes: ['daily', 'dailySum', 'dedup'],
    build(params) {
      return buildStayTimeSql(params)
    }
  },
  return1: {
    modes: ['daily', 'dailySum', 'dedup'],
    build(params) {
      return buildReturnVisitSql({
        ...params,
        windowStartOffset: -1,
        windowEndOffset: -1,
        title: '次日回访'
      })
    }
  },
  return7: {
    modes: ['daily', 'dailySum', 'dedup'],
    build(params) {
      return buildReturnVisitSql({
        ...params,
        windowStartOffset: -6,
        windowEndOffset: -1,
        title: '7日回访'
      })
    }
  },
  orderCrowd: {
    modes: ['single'],
    build(params) {
      return buildOrderCrowdSql(params)
    }
  }
}

export function buildProjectExportTasks(options) {
  const {
    project,
    startDate,
    endDate,
    selectedContents = [],
    selectedModes = [],
    byChannel = false,
    clickKeyword = '点击'
  } = options

  const projectCode = project?.code || ''
  const version = project?.version || 'v4'
  const tasks = []
  const includeDailySum = selectedModes.includes('dailySum')
  const includeDedup = selectedModes.includes('dedup')

  selectedContents.forEach((contentKey) => {
    const content = CONTENT_BUILDERS[contentKey]
    if (!content) return

    if (contentKey === 'orderCrowd') {
      const sql = content.build({ projectCode, version, startDate, endDate })
      tasks.push({
        id: `${contentKey}_single_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        contentKey,
        mode: 'single',
        label: '抽订单人群 SQL',
        sql,
        includeDailySum,
        includeDedup: false,
        copyOnly: true
      })
      return
    }

    const dailySql = stripSqlTail(content.build({
      projectCode,
      version,
      startDate,
      endDate,
      mode: 'daily',
      byChannel,
      clickKeyword
    }))
    const sqlParts = [dailySql]

    if (includeDedup && content.modes.includes('dedup')) {
      sqlParts.push(stripSqlTail(content.build({
        projectCode,
        version,
        startDate,
        endDate,
        mode: 'dedup',
        byChannel,
        clickKeyword
      })))
    }

    tasks.push({
      id: `${contentKey}_main_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      contentKey,
      mode: 'daily',
      label: resolveContentLabel(contentKey),
      sql: `${sqlParts.join('\nUNION ALL\n')}\n;`,
      includeDailySum,
      includeDedup,
      copyOnly: false
    })
  })

  return tasks
}

function stripSqlTail(sql) {
  return String(sql || '').trim().replace(/;$/, '')
}

function resolveContentLabel(key) {
  return PROJECT_EXPORT_CONTENTS.find((item) => item.key === key)?.label || key
}

function resolveModeLabel(key) {
  if (key === 'single') return '原始 SQL'
  return PROJECT_EXPORT_MODES.find((item) => item.key === key)?.label || key
}
